import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Bell, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import useRealtime from "@/lib/realtime";
import { useDesktop } from "@/lib/desktop";
import { playDing, isMuted, setMuted } from "@/lib/sound";

const TYPE_DOTS = {
    new_booking: "bg-emerald-500",
    booking_modified: "bg-amber-500",
    booking_cancelled: "bg-red-500",
    payment_received: "bg-emerald-500",
    payment_failed: "bg-red-500",
    check_in_reminder: "bg-blue-500",
    check_out_reminder: "bg-blue-500",
    housekeeping_task: "bg-violet-500",
    room_maintenance: "bg-orange-500",
    ota_sync_failed: "bg-red-500",
    guest_message: "bg-orange-500",
    review_request: "bg-amber-500",
};

function ago(iso) {
    if (!iso) return "";
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
}

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [muted, setMutedState] = useState(() => isMuted());
    const [coords, setCoords] = useState({ top: 0, right: 0 });
    const ref = useRef(null);
    const btnRef = useRef(null);
    const navigate = useNavigate();
    const desktop = useDesktop();

    const load = async () => {
        try {
            const [list, cnt] = await Promise.all([
                api.get("/notifications?limit=20"),
                api.get("/notifications/unread-count"),
            ]);
            setItems(list.data);
            setCount(cnt.data.count);
        } catch { /* ignore */ }
    };

    useEffect(() => { load(); }, []);
    useRealtime((msg) => {
        if (msg?.event === "notification.created") {
            load();
            // Backend sends { event, hotel_id, payload } — earlier code read `msg.data`
            // which was always undefined, so no toast/sound/OS notification ever fired.
            const n = msg.payload || msg.data || {};
            // Native OS notification (Electron / Web Notifications)
            desktop.notify({
                title: n.title || "Akomoda",
                body: n.body || "",
            });
            // Soft "ding" for the events staff most needs to hear immediately.
            // Requires a prior user gesture in the tab for autoplay policies;
            // playDing() no-ops silently if the audio context isn't ready.
            const audibleTypes = new Set(["new_booking", "booking_modified", "booking_cancelled"]);
            if (audibleTypes.has(n.type)) playDing();
            // Also fire an on-screen toast so staff can't miss a fresh booking
            // even if they're not looking at the bell in the topbar.
            if (n.type === "new_booking") {
                toast.success(n.title || "New booking", {
                    description: n.body,
                    duration: 8000,
                    action: n.link ? { label: "Open", onClick: () => navigate(n.link) } : undefined,
                });
            } else if (n.type === "booking_cancelled") {
                toast.error(n.title || "Booking cancelled", { description: n.body, duration: 6000 });
            } else if (n.type === "booking_modified") {
                toast.info(n.title || "Booking modified", { description: n.body, duration: 6000 });
            }
        }
    });

    useEffect(() => {
        const onDoc = (e) => {
            if (!ref.current) return;
            // Treat both the trigger container AND the portal dropdown as "inside".
            if (ref.current.contains(e.target)) return;
            const portal = ref.current.__portal;
            if (portal && portal.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const markAll = async () => {
        // Optimistic UI update — clear the badge immediately.
        setCount(0);
        setItems((prev) => prev.map((x) => ({ ...x, read: true })));
        try {
            await api.post("/notifications/mark-all-read");
        } catch { /* ignore */ }
        await load();
    };

    const openItem = async (n) => {
        // Close the dropdown FIRST so it doesn't stay open behind the router navigation.
        setOpen(false);
        // Optimistic: decrement count + mark the item read locally before the
        // network round-trip so the red badge disappears immediately.
        if (!n.read) {
            setCount((c) => Math.max(0, c - 1));
            setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
        }
        try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
        if (n.link) navigate(n.link);
        // Reconcile with the server in the background (in case optimistic drift).
        load();
    };

    // Recompute dropdown coords whenever it opens (so scrolling the topbar horizontally
    // on mobile doesn't leave a stale position behind).
    useEffect(() => {
        if (!open) return;
        const update = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + 6, right: Math.max(6, window.innerWidth - r.right) });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button ref={btnRef} onClick={() => setOpen(!open)} data-testid="notif-bell"
                    className="relative rounded-md border border-border bg-card p-1.5 text-muted-foreground hover:text-foreground"
                    title="Notifications">
                <Bell size={14} />
                {count > 0 && !open && (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold text-white"
                          data-testid="notif-badge">
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>
            {open && createPortal(
                <div
                    className="fixed z-[80] w-[360px] max-w-[calc(100vw-1rem)] rounded-md border border-border bg-card shadow-lg"
                    style={{ top: coords.top, right: coords.right }}
                    data-testid="notif-dropdown"
                    ref={(el) => {
                        // Register this portal element so the "click outside" handler treats it as inside the widget.
                        if (ref.current) ref.current.__portal = el;
                    }}
                >
                    <div className="flex items-center justify-between border-b border-border px-3 py-2">
                        <div className="text-[12px] font-semibold tracking-tight">Notifications</div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    const next = !muted;
                                    setMutedState(next);
                                    setMuted(next);
                                    if (!next) playDing(); // preview the ding when enabling
                                }}
                                data-testid="notif-sound-toggle"
                                title={muted ? "Enable sound" : "Mute sound"}
                                className="text-muted-foreground transition hover:text-foreground"
                            >
                                {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                            </button>
                            {count > 0 && (
                                <button onClick={markAll} data-testid="notif-mark-all"
                                        className="text-[11px] font-medium text-[#ff7b02] hover:underline">
                                    Mark all read
                                </button>
                            )}
                            <button onClick={() => { setOpen(false); navigate("/inbox"); }}
                                    className="text-[11px] font-medium text-muted-foreground hover:text-foreground">
                                Inbox →
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[480px] overflow-y-auto">
                        {items.length === 0 && (
                            <div className="py-10 text-center text-[12px] text-muted-foreground">No notifications</div>
                        )}
                        {items.map((n) => (
                            <button key={n.id} onClick={() => openItem(n)}
                                    data-testid={`notif-item-${n.id}`}
                                    className={`flex w-full items-start gap-2 border-b border-border px-3 py-2 text-left hover:bg-muted/40 ${
                                        !n.read ? "bg-[#ff7b02]/3" : ""
                                    }`}>
                                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${TYPE_DOTS[n.type] || "bg-slate-400"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="truncate text-[12.5px] font-medium">{n.title}</div>
                                        <div className="text-[10px] text-muted-foreground shrink-0">{ago(n.created_at)}</div>
                                    </div>
                                    {n.body && <div className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{n.body}</div>}
                                </div>
                                {!n.read && <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#ff7b02] shrink-0" />}
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
